import {
	Button,
	ButtonGroup,
	Card,
	IconButton,
	LuIcon,
	Menu,
	SegmentedTabs,
	Separator,
	Text,
	toast,
} from "@overmap-ai/blocks"
import {
	type Asset,
	type AssetAttachment,
	type AssetTypeFields,
	EMPTY_ARRAY,
	ERROR_MSG_UNEXPECTED,
	type Form,
	type FormSubmission,
	type Issue,
	type IssueType,
	OrganizationAccessLevel,
	type ProcedureType,
	type Stored,
	submittedAtModelSortFn,
	toUuidArray,
} from "@overmap-ai/common"
import {
	APIError,
	selectAssetById,
	selectAssetTypeFieldsById,
	selectAssetTypeFieldsOfAssetType,
	selectAssetTypeIdentifiersOfAssetType,
	selectAssetTypeStatusesOfAssetType,
	selectAttachmentsOfAsset,
	selectAttachmentsOfAssetTypeFieldValues,
	selectFormsOfOrganization,
	selectFormSubmissionsOfAsset,
	selectFormSubmissionsOfProject,
	selectIssueAssociationsOfAsset,
	selectIssuesOfProject,
	selectIssueTypesOfOrganization,
	selectLatestAssetTypeFieldsOfAssetType,
	selectProceduresOfAsset,
	selectProcedureTypesOfOrganization,
} from "@overmap-ai/core"
import type { FieldValues } from "@overmap-ai/forms"
import { useOpenState, useSort, useSyncedState } from "@overmap-ai/hooks"
import { useNavigate } from "@tanstack/react-router"
import type { ChainedCommands } from "@tiptap/core"
import { Placeholder } from "@tiptap/extensions"
import { EditorContent, useEditor } from "@tiptap/react"
import * as turf from "@turf/turf"
import React, { type FocusEventHandler, memo, useCallback } from "react"

import { TITLE_MAX_LENGTH } from "../../constants"
import { useGeometryPicker } from "../../contexts/hooks"
import { useAsset, useDeleteAsset } from "../../hooks/assets"
import { useFormSubmissionsWithIdentifiers } from "../../hooks/forms"
import { useIssuesWithIdentifiers } from "../../hooks/issues"
import { useUserOrganizationAccess } from "../../hooks/users"
import { useBlurInput, useConfirmDeleteDialog } from "../../hooks/utils"
import { useViewGeometryOnMap } from "../../mapbox/hooks"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"
import {
	selectActiveOrganizationId,
	selectActiveProjectId,
	selectLatestAssetTypeFieldValuesOfAsset,
} from "../../store/selectors"
import {
	AssetAttachmentNode,
	BASE_EXTENSIONS,
	LINK_NODES,
	MENTION_EXTENSIONS,
	useEditorBlur,
	useEditorContent,
} from "../../tiptap"
import { NewLineFloatingMenu, TextBubbleMenu } from "../../tiptap/components"
import { EditorContext } from "../../tiptap/context"
import { AssetFileHandler } from "../../tiptap/extensions"
import { BaseAttachmentNode } from "../../tiptap/nodes/BaseAttachment/extension"
import type { AssetPdfExportOptions } from "../../typings/reports"
import { AssetTimeline } from "../AssetTimeline/AssetTimeline"
import { AssetTypeIdentifiersList } from "../AssetTypeIdentifiersList/AssetTypeIdentifiersList"
import { AttachmentList } from "../AttachmentList/AttachmentList"
import { AttachmentButton } from "../Buttons/AttachmentButton"
import { FormSubmissionCommandMenu } from "../CommandMenus/FormSubmissionCommandMenu"
import { IssueCommandMenu } from "../CommandMenus/IssueCommandMenu"
import { AssetPdfExportOptionsDialog } from "../Dialogs/AssetPdfExportOptionsDialog"
import { AssetTypeDescriptionDialog } from "../Dialogs/AssetTypeDescriptionDialog"
import { AssetTypeStatusSelect } from "../DropdownSelects/AssetTypeStatusSelect"
import { FieldsRevisionDropdownSelect } from "../DropdownSelects/FieldsRevisionDropdownSelect"
import { ProcedureTypeDropdownSelect } from "../DropdownSelects/ProcedureTypeDropdownSelect"
import { AssetTypeFieldsSubmitter } from "../Form/AssetTypeFieldsSubmitter"
import {
	useDeserializeFieldSchema,
	useDeserializeFieldValues,
	useFieldValuesChangeDebounced,
	useHydrateFieldValuesWithAttachments,
} from "../Form/hooks"
import {
	getAttachmentPayloadsFromFilesAndPromises,
	getFieldFilesAndPromisesToCreate,
	getFieldValuesAttachmentsToDelete,
	getIsFieldValuesDraft,
} from "../Form/utils"
import { FormSubmissionList } from "../FormSubmissionList/FormSubmissionList"
import { AssetTypeIcon } from "../Icons/AssetTypeIcon"
import { AssetTypeStatusIcon } from "../Icons/AssetTypeStatusIcon"
import { AssetIndexLabel } from "../IndexLabels/AssetIndexLabel"
import { IssueAssociationList } from "../IssueAssociationList/IssueAssociationList"
import { GeometryGroup } from "../MenuGroups/GeometryGroup"
import { FileInputMenuItem } from "../MenuItems/FileInputMenuItem"
import { OrganizationAccessRequired } from "../OrganizationAccessRequired/AccessRequired"
import { ProcedureList } from "../ProcedureList/ProcedureList"

enum AssetDetailsViewerTabs {
	OVERVIEW = "overview",
	ATTACHMENTS = "attachments",
}

export interface AssetDetailsViewerProps {
	assetId: Asset["uuid"]
}

export const AssetDetailsViewer = memo((props: AssetDetailsViewerProps) => {
	const { assetId } = props

	const asset = useAppSelector(selectAssetById(assetId))

	if (!asset) {
		throw new Error(`Asset not found for assetId ${assetId}`)
	}

	const { assetType, assetTypeStatus, handleLabelChange, handleDescriptionChange, handleStatusChange } =
		useAsset(asset)
	const activeProjectId = useAppSelector(selectActiveProjectId)!
	const activeOrganizationId = useAppSelector(selectActiveOrganizationId)!
	const navigate = useNavigate()
	const deleteAsset = useDeleteAsset()
	const openConfirmDeleteDialog = useConfirmDeleteDialog()
	const openGeometryPicker = useGeometryPicker()
	const viewGeometryOnMap = useViewGeometryOnMap()
	const isUserAtLeastBasic = useUserOrganizationAccess(OrganizationAccessLevel.BASIC)
	const [showIssueCommandMenu, openIssueCommandMenu, closeIssueCommandMenu] = useOpenState(false)
	const [showFormCommandMenu, openFormCommandMenu, closeFormCommandMenu] = useOpenState(false)
	const [showPdfExportOptionsDialog, openPdfExportOptionsDialog, closePdfExportOptionsDialog] = useOpenState(false)
	const issueAssociations = useShallowEqaulSelector(selectIssueAssociationsOfAsset(asset.uuid))
	const formSubmissions = useShallowEqaulSelector(selectFormSubmissionsOfAsset(asset.uuid))
	const projectFormSubmissions = useShallowEqaulSelector(selectFormSubmissionsOfProject(activeProjectId))
	const formSubmissionsAndIdentifiers = useFormSubmissionsWithIdentifiers(projectFormSubmissions)
	const attachments = useShallowEqaulSelector(selectAttachmentsOfAsset(asset.uuid))
	const latestAssetTypeFields = useAppSelector(selectLatestAssetTypeFieldsOfAssetType(assetType.uuid))
	const assetTypeFields = useShallowEqaulSelector(selectAssetTypeFieldsOfAssetType(assetType.uuid))
	const assetTypeFieldValues = useAppSelector(selectLatestAssetTypeFieldValuesOfAsset(asset.uuid))
	const currentAssetTypeFields = useAppSelector(
		selectAssetTypeFieldsById(assetTypeFieldValues?.fields_revision ?? ""),
	)
	const assetTypeFieldValuesAttachments = useShallowEqaulSelector(
		selectAttachmentsOfAssetTypeFieldValues(assetTypeFieldValues?.uuid ?? ""),
	)
	const procedures = useShallowEqaulSelector(selectProceduresOfAsset(asset.uuid))
	const procedureTypes = useShallowEqaulSelector(selectProcedureTypesOfOrganization(activeOrganizationId))
	const forms = useShallowEqaulSelector(selectFormsOfOrganization(activeOrganizationId))
	const assetTypeIdentifiers = useShallowEqaulSelector(selectAssetTypeIdentifiersOfAssetType(assetType.uuid))
	const assetTypeStatuses = useShallowEqaulSelector(selectAssetTypeStatusesOfAssetType(assetType.uuid))
	const issueTypes = useShallowEqaulSelector(selectIssueTypesOfOrganization(activeOrganizationId))
	const issues = useShallowEqaulSelector(selectIssuesOfProject(activeProjectId))
	const issuesAndIdentifiers = useIssuesWithIdentifiers(issues)

	const [computedAssetTypeFields, setComputedAssetTypeFields] = useSyncedState(
		currentAssetTypeFields ?? latestAssetTypeFields,
	)

	const handleDeleteAsset = useCallback(() => {
		deleteAsset(asset.uuid, async () => {
			await navigate({
				from: "/$organizationId/$projectId",
				to: "/$organizationId/$projectId",
				search: {},
				replace: true,
			})
		})
	}, [asset.uuid, deleteAsset, navigate])

	const handleAssetLabelTextAreaBlur = useCallback<FocusEventHandler<HTMLTextAreaElement>>(
		(e) => {
			void handleLabelChange(e.target.value)
		},
		[handleLabelChange],
	)

	const handleGeometryChange = useCallback(() => {
		viewGeometryOnMap(asset.geometry)

		openGeometryPicker((close) => ({
			initialGeometry: asset.geometry,
			onSave: (geometry) => {
				close()

				void sdk.assets.update({
					uuid: asset.uuid,
					geometry: geometry,
				})
			},
			onCancel: close,
		}))
	}, [asset.geometry, asset.uuid, openGeometryPicker, viewGeometryOnMap])

	// Attachments logic

	const handleAttachFilesToAsset = useCallback(
		(files: File[]) => {
			return sdk.assetAttachments.bulkAdd(
				files.map((file) => ({
					asset: asset.uuid,
					file,
				})),
			)
		},
		[asset.uuid],
	)

	const handleDeleteAssetAttachment = useCallback(
		(fileAttachment: Stored<AssetAttachment>) => {
			openConfirmDeleteDialog({
				title: "Delete attachment",
				description: "The attachment will be deleted immediately. This action cannot be undone.",
				onAction: () => sdk.assetAttachments.delete(fileAttachment.uuid),
			})
		},
		[openConfirmDeleteDialog],
	)

	const handleApplyProcedure = useCallback(
		(procedureType?: Stored<ProcedureType>) => {
			if (!procedureType) return

			const [_offlineProcedure, _promise] = sdk.procedures.add({
				procedure_type: procedureType.uuid,
				project: asset.project,
				procedure_initiative: null,
				asset: asset.uuid,
			})
		},
		[asset.project, asset.uuid],
	)

	const handleView = useCallback(() => {
		void navigate({
			from: "/$organizationId/$projectId",
			to: "/$organizationId/$projectId/assets/$assetId",
			params: (prev) => {
				return {
					...prev,
					assetId: asset.uuid,
				}
			},
		})
	}, [asset.uuid, navigate])

	const assetLabelInputProps = useBlurInput<HTMLTextAreaElement>(
		asset.label || "",
		handleAssetLabelTextAreaBlur,
		true,
	)

	const handleLinkIssue = useCallback(
		(issue: Stored<Issue>) => {
			const [_, promise] = sdk.issueAssociations.add({
				asset: asset.uuid,
				issue: issue.uuid,
			})

			void promise.then(() => {
				toast.success({
					title: "Success",
					description: "Successfully linked issue to asset.",
				})
			})
		},
		[asset.uuid],
	)

	const handleCreateIssueAndLink = useCallback(
		(issueType: Stored<IssueType>) => {
			const [offlineIssue, _promise] = sdk.issues.add({
				title: "",
				description: null,
				assigned_to: null,
				due_date: null,
				issue_type: issueType.uuid,
				geo_marker: turf.centroid(asset.geometry).geometry,
				priority: issueType.default_priority!,
				status: issueType.default_status!,
				project: asset.project,
			})

			handleLinkIssue(offlineIssue)

			void navigate({
				from: "/$organizationId/$projectId",
				to: "/$organizationId/$projectId",
				search: { issue: offlineIssue.uuid },
			})
		},
		[asset.geometry, asset.project, handleLinkIssue, navigate],
	)

	const handleViewFormSubmission = useCallback(
		(formSubmission: Stored<FormSubmission>) => {
			void navigate({
				from: "/$organizationId/$projectId",
				to: "/$organizationId/$projectId/forms/submissions/$formSubmissionId",
				params: (prev) => ({ ...prev, formSubmissionId: formSubmission.uuid }),
			})
		},
		[navigate],
	)

	const handleUnlinkFormSubmission = useCallback(
		(formSubmission: Stored<FormSubmission>) => {
			openConfirmDeleteDialog({
				title: "Unlink form submission",
				description: "The form submission will be unlinked. This action cannot be undone.",
				onAction: () => sdk.formSubmissions.update({ uuid: formSubmission.uuid, asset: null, issue: null }),
			})
		},
		[openConfirmDeleteDialog],
	)

	const handleDeleteFormSubmission = useCallback(
		(formSubmission: Stored<FormSubmission>) => {
			openConfirmDeleteDialog({
				title: "Delete form submission",
				description: "The form submission will be deleted immediately. This action cannot be undone.",
				onAction: () => sdk.formSubmissions.delete(formSubmission.uuid),
			})
		},
		[openConfirmDeleteDialog],
	)

	const handleSelectFormSubmission = useCallback(
		(formSubmission: Stored<FormSubmission>) => {
			const [_, promise] = sdk.formSubmissions.update({
				uuid: formSubmission.uuid,
				asset: asset.uuid,
				issue: null,
			})

			void promise.then(() => {
				toast.success({
					title: "Success",
					description: "Successfully linked form submission to asset.",
				})
			})
		},
		[asset.uuid],
	)

	const handleCreateFormSubmission = useCallback(
		(form: Stored<Form>) => {
			void navigate({
				from: "/$organizationId/$projectId",
				to: "/$organizationId/$projectId/forms/$formId/submit",
				params: (prev) => ({ ...prev, formId: form.uuid }),
				search: {
					asset: asset.uuid,
				},
			})
		},
		[navigate, asset.uuid],
	)

	const handleExportAssetToPdf = useCallback(
		(options: AssetPdfExportOptions) => {
			void sdk.assets.getAssetPdf(asset.uuid, options).catch((e) => {
				toast.error({
					title: "Failed to download PDF",
					description: e instanceof APIError ? e.message : ERROR_MSG_UNEXPECTED,
				})
			})
		},
		[asset.uuid],
	)

	const editorContent = useEditorContent(asset.description)
	const handleEditorBlur = useEditorBlur(editorContent, handleDescriptionChange)

	const editor = useEditor(
		{
			content: editorContent,
			editable: isUserAtLeastBasic,
			editorProps: {
				attributes: { class: "outline-none h-max min-h-[80px]" },
			},
			onBlur: handleEditorBlur,
			extensions: [
				...BASE_EXTENSIONS,
				...MENTION_EXTENSIONS,
				...LINK_NODES,
				AssetAttachmentNode,
				AssetFileHandler.configure({
					uuid: asset.uuid,
				}),
				Placeholder.configure({
					placeholder: "Enter description",
				}),
			],
		},
		[asset.uuid, isUserAtLeastBasic, handleEditorBlur],
	)

	const handleInsertAttachments = useCallback(
		(chainedCommands: () => ChainedCommands, files: File[]) => {
			void handleAttachFilesToAsset(files).then(([offlineAttachments]) => {
				chainedCommands()
					.insertContent(
						offlineAttachments.map((offlineAttachment) => ({
							type: BaseAttachmentNode.name,
							attrs: {
								uuid: offlineAttachment.uuid,
							},
						})),
					)
					.run()
			})
		},
		[handleAttachFilesToAsset],
	)

	const fieldSchema = useDeserializeFieldSchema(computedAssetTypeFields?.fields ?? EMPTY_ARRAY)

	const isAssetTypeFieldValuesDraft = getIsFieldValuesDraft(assetTypeFieldValues)

	const handleAssetTypeFieldValuesChange = useFieldValuesChangeDebounced(
		(allValues: FieldValues) => {
			if (!computedAssetTypeFields?.uuid) return

			if (!isAssetTypeFieldValuesDraft || !assetTypeFieldValues?.uuid) {
				const [values, filesAndPromises] = fieldSchema.extractFilesAndPromisesFromFieldValues(allValues)

				const [offlineFieldValues, _promise] = sdk.assetTypeFieldValues.add({
					fields_revision: computedAssetTypeFields.uuid,
					asset: asset.uuid,
					values: fieldSchema.serializeValues(values),
					published_at: null,
				})

				if (Object.keys(filesAndPromises).length !== 0) {
					void sdk.assetTypeFieldValuesAttachments.bulkAdd(
						getAttachmentPayloadsFromFilesAndPromises(
							offlineFieldValues.uuid,
							filesAndPromises,
							assetTypeFieldValuesAttachments,
						),
					)
				}
			} else {
				const [values, filesAndPromises] = fieldSchema.extractFilesAndPromisesFromFieldValues(allValues)

				const [offlineFieldValues, _promise] = sdk.assetTypeFieldValues.update({
					uuid: assetTypeFieldValues.uuid,
					fields_revision: computedAssetTypeFields.uuid,
					values: fieldSchema.serializeValues(values),
				})

				const attachmentsToDelete = getFieldValuesAttachmentsToDelete(
					assetTypeFieldValuesAttachments,
					filesAndPromises,
				)

				const filesAndPromisesToCreate = getFieldFilesAndPromisesToCreate(
					assetTypeFieldValuesAttachments,
					filesAndPromises,
				)

				if (attachmentsToDelete.length !== 0) {
					void sdk.assetTypeFieldValuesAttachments.bulkDelete(toUuidArray(attachmentsToDelete))
				}

				if (Object.keys(filesAndPromisesToCreate).length !== 0) {
					void sdk.assetTypeFieldValuesAttachments.bulkAdd(
						getAttachmentPayloadsFromFilesAndPromises(
							offlineFieldValues.uuid,
							filesAndPromisesToCreate,
							assetTypeFieldValuesAttachments,
						),
					)
				}
			}
		},
		[
			computedAssetTypeFields?.uuid,
			isAssetTypeFieldValuesDraft,
			asset.uuid,
			fieldSchema,
			assetTypeFieldValues?.uuid,
			assetTypeFieldValuesAttachments,
		],
	)

	const handleAssetTypeFieldValuesSubmit = useCallback(
		(_values: FieldValues) => {
			if (!assetTypeFieldValues?.uuid) return

			handleAssetTypeFieldValuesChange.flush()

			const [_offlineAssetTypeFieldValues, _promise] = sdk.assetTypeFieldValues.update({
				uuid: assetTypeFieldValues.uuid,
				published_at: new Date().toISOString(),
			})
		},
		[assetTypeFieldValues?.uuid, handleAssetTypeFieldValuesChange],
	)

	const handleDeleteAssetTypeFieldValues = useCallback(() => {
		if (!assetTypeFieldValues?.uuid || !isAssetTypeFieldValuesDraft) return

		openConfirmDeleteDialog({
			title: "Delete draft",
			description: "Are you sure you want to delete this draft? This action cannot be undone.",
			onAction: () => {
				handleAssetTypeFieldValuesChange.flush()
				void sdk.assetTypeFieldValues.delete(assetTypeFieldValues.uuid)
			},
		})
	}, [
		isAssetTypeFieldValuesDraft,
		assetTypeFieldValues?.uuid,
		openConfirmDeleteDialog,
		handleAssetTypeFieldValuesChange,
	])

	const handleAssetTypeFieldsSelect = useCallback(
		(assetTypeFields: Stored<AssetTypeFields>) => {
			handleAssetTypeFieldValuesChange.flush()
			setComputedAssetTypeFields(assetTypeFields)
		},
		[handleAssetTypeFieldValuesChange, setComputedAssetTypeFields],
	)

	const fieldValues = useDeserializeFieldValues(fieldSchema, assetTypeFieldValues?.values)

	const fieldValuesWithAttachments = useHydrateFieldValuesWithAttachments(
		fieldSchema,
		fieldValues,
		assetTypeFieldValuesAttachments,
		sdk.assetTypeFieldValuesAttachments,
	)

	const sortedProcedures = useSort(procedures, submittedAtModelSortFn)
	const sortedIssueAssociations = useSort(issueAssociations, submittedAtModelSortFn)
	const sortedFormSubmissions = useSort(formSubmissions, submittedAtModelSortFn)
	const sortedAttachments = useSort(attachments, submittedAtModelSortFn)

	const attachmentsCount =
		attachments.length + issueAssociations.length + procedures.length + sortedFormSubmissions.length

	return (
		<>
			<div className="flex size-full flex-col gap-2 overflow-hidden p-4">
				<div className="flex items-center justify-between gap-4">
					<div className="flex min-w-0 gap-1">
						<div className="flex min-w-0 items-center gap-2">
							<AssetTypeIcon assetTypeId={assetType.uuid} />
							<Text className="truncate">{assetType.name}</Text>
							<AssetIndexLabel assetId={asset.uuid} className="truncate" accentColor="base" />
						</div>
						<AssetTypeDescriptionDialog assetType={assetType} size="sm">
							<IconButton variant="ghost" accentColor="base" size="sm">
								<LuIcon icon={modelIconMapping.info} />
							</IconButton>
						</AssetTypeDescriptionDialog>
					</div>
					<ButtonGroup className="flex items-center gap-2" size="sm" variant="soft" accentColor="base">
						<Menu.Root>
							<Menu.ClickTrigger>
								<IconButton variant="ghost">
									<LuIcon icon={modelIconMapping.more} />
								</IconButton>
							</Menu.ClickTrigger>
							<Menu.Content size="sm">
								<Menu.Pages defaultPage="initial">
									<Menu.PageContent page="initial">
										<Menu.Item onClick={handleView}>
											<LuIcon icon={modelIconMapping.expand} />
											View
										</Menu.Item>
										<Menu.Item onSelect={openIssueCommandMenu}>
											<LuIcon icon={modelIconMapping.issue} />
											Link issue
										</Menu.Item>
										<Menu.Item onSelect={openFormCommandMenu}>
											<LuIcon icon={modelIconMapping.form} />
											Link form
										</Menu.Item>
										<FileInputMenuItem onAttachFiles={handleAttachFilesToAsset} />
										<Menu.PageTrigger page="geometry">
											<LuIcon icon={modelIconMapping.geometry} />
											Geometry
										</Menu.PageTrigger>
										<Menu.Item onClick={openPdfExportOptionsDialog}>
											<LuIcon icon={modelIconMapping.pdf} />
											Export to PDF
										</Menu.Item>
										<OrganizationAccessRequired
											accessLevel={OrganizationAccessLevel.ADMIN}
											type="hide"
										>
											<Menu.Separator />
											<Menu.Item onClick={handleDeleteAsset}>
												<LuIcon icon={modelIconMapping.delete} />
												Delete
											</Menu.Item>
										</OrganizationAccessRequired>
									</Menu.PageContent>
									<Menu.PageContent page="geometry">
										<GeometryGroup
											geometry={asset.geometry}
											onGeometryChange={handleGeometryChange}
										/>
									</Menu.PageContent>
								</Menu.Pages>
							</Menu.Content>
						</Menu.Root>
					</ButtonGroup>
				</div>

				<SegmentedTabs.Root
					defaultValue={AssetDetailsViewerTabs.OVERVIEW}
					className="flex grow flex-col gap-4 overflow-hidden"
				>
					<SegmentedTabs.List size="sm" accentColor="base" variant="surface">
						<SegmentedTabs.Trigger className="basis-1" value={AssetDetailsViewerTabs.OVERVIEW}>
							Overview
						</SegmentedTabs.Trigger>
						<SegmentedTabs.Trigger className="basis-1" value={AssetDetailsViewerTabs.ATTACHMENTS}>
							Attachments&nbsp;
							{attachmentsCount !== 0 && `(${attachmentsCount})`}
						</SegmentedTabs.Trigger>
					</SegmentedTabs.List>

					<SegmentedTabs.Content
						value={AssetDetailsViewerTabs.OVERVIEW}
						className="flex grow flex-col gap-4 overflow-y-auto"
					>
						<div className="flex flex-col gap-2">
							<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.BASIC} type="disable">
								<textarea
									className="field-sizing-content placeholder-(--base-a9) w-full resize-none text-lg font-bold outline-none"
									placeholder="Enter a label"
									maxLength={TITLE_MAX_LENGTH}
									{...assetLabelInputProps}
								/>
							</OrganizationAccessRequired>

							<EditorContext value={editor}>
								<EditorContent editor={editor} />
								<TextBubbleMenu editor={editor} />
								<NewLineFloatingMenu editor={editor} onAttachFiles={handleInsertAttachments} />
							</EditorContext>

							<ButtonGroup
								className="flex w-full flex-wrap items-center gap-2"
								size="sm"
								variant="soft"
								accentColor="base"
							>
								<AssetTypeStatusSelect
									assetTypeStatuses={assetTypeStatuses}
									required
									value={assetTypeStatus}
									onValueChange={handleStatusChange}
									size="sm"
								>
									<Button>
										<AssetTypeStatusIcon assetTypeStatusId={asset.status} />
										{assetTypeStatus?.name}
									</Button>
								</AssetTypeStatusSelect>

								<Button onClick={openIssueCommandMenu}>
									<LuIcon icon={modelIconMapping.issue} />
									Link issue
								</Button>

								<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.BASIC} type="hide">
									<ProcedureTypeDropdownSelect
										required={false}
										procedureTypes={procedureTypes}
										onValueChange={handleApplyProcedure}
										size="sm"
									>
										<Button>
											<LuIcon icon={modelIconMapping.add} />
											Procedure
										</Button>
									</ProcedureTypeDropdownSelect>
								</OrganizationAccessRequired>

								<Button onClick={openFormCommandMenu}>
									<LuIcon icon={modelIconMapping.form} />
									Link form
								</Button>

								<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.BASIC} type="hide">
									<AttachmentButton onAttachFiles={handleAttachFilesToAsset}>
										<LuIcon icon={modelIconMapping.attachment} />
										Attach
									</AttachmentButton>
								</OrganizationAccessRequired>
							</ButtonGroup>
						</div>

						{assetTypeIdentifiers.length > 0 ? (
							<AssetTypeIdentifiersList asset={asset} assetTypeIdentifiers={assetTypeIdentifiers} />
						) : null}

						{computedAssetTypeFields ? (
							<Card className="flex flex-col gap-4" variant="soft" size="sm">
								<FieldsRevisionDropdownSelect
									required
									disabled={isAssetTypeFieldValuesDraft}
									fieldsRevisions={assetTypeFields}
									current={currentAssetTypeFields}
									value={computedAssetTypeFields}
									onValueChange={handleAssetTypeFieldsSelect}
									size="sm"
								>
									<Button
										size="xs"
										accentColor="base"
										variant="surface"
										className="w-max"
										disabled={isAssetTypeFieldValuesDraft}
									>
										Revision {computedAssetTypeFields.revision}
										<LuIcon icon={modelIconMapping.chevronDown} />
									</Button>
								</FieldsRevisionDropdownSelect>

								<AssetTypeFieldsSubmitter
									size="sm"
									key={assetTypeFieldValues?.uuid}
									assetTypeFields={computedAssetTypeFields}
									onSubmit={handleAssetTypeFieldValuesSubmit}
									values={fieldValuesWithAttachments}
									onValuesChange={handleAssetTypeFieldValuesChange}
									hideCancel={!isAssetTypeFieldValuesDraft}
									cancelText="Draft"
									cancelIcon={modelIconMapping.delete}
									onCancel={handleDeleteAssetTypeFieldValues}
									disabled={!isUserAtLeastBasic}
									hideSubmit={!isAssetTypeFieldValuesDraft}
								/>
							</Card>
						) : null}

						<Separator size="full" />

						<AssetTimeline asset={asset} size="xs" />
					</SegmentedTabs.Content>
					<SegmentedTabs.Content
						value={AssetDetailsViewerTabs.ATTACHMENTS}
						className="flex grow flex-col gap-4 overflow-y-auto"
					>
						<div className="flex flex-col gap-2">
							<div className="flex justify-between gap-2">
								<Text size="sm">Procedures</Text>

								<ProcedureTypeDropdownSelect
									required={false}
									procedureTypes={procedureTypes}
									onValueChange={handleApplyProcedure}
									size="sm"
								>
									<IconButton size="xs" accentColor="base" variant="soft">
										<LuIcon icon={modelIconMapping.add} />
									</IconButton>
								</ProcedureTypeDropdownSelect>
							</div>
							<ProcedureList procedures={sortedProcedures} readonly={!isUserAtLeastBasic} />
						</div>

						<Separator size="full" />

						<div className="flex flex-col gap-2">
							<div className="flex justify-between gap-2">
								<Text size="sm">Issues</Text>

								<IconButton onClick={openIssueCommandMenu} size="xs" accentColor="base" variant="soft">
									<LuIcon icon={modelIconMapping.add} />
								</IconButton>
							</div>
							<IssueAssociationList issueAssociations={sortedIssueAssociations} type="issue" />
						</div>

						<Separator size="full" />

						<div className="flex flex-col gap-2">
							<div className="flex justify-between gap-2">
								<Text size="sm">Forms</Text>
								<IconButton onClick={openFormCommandMenu} size="xs" accentColor="base" variant="soft">
									<LuIcon icon={modelIconMapping.add} />
								</IconButton>
							</div>
							<FormSubmissionList
								formSubmissions={sortedFormSubmissions}
								onClickFormSubmission={handleViewFormSubmission}
								onViewFormSubmission={handleViewFormSubmission}
								onUnlinkFormSubmission={handleUnlinkFormSubmission}
								onDeleteFormSubmission={handleDeleteFormSubmission}
							/>
						</div>

						<Separator size="full" />

						<section className="flex h-max w-full flex-col gap-2">
							<div className="flex h-max w-full items-center justify-between">
								<Text size="sm">Attachments</Text>
								<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.BASIC} type="hide">
									<AttachmentButton
										icon
										accentColor="base"
										size="xs"
										variant="soft"
										onAttachFiles={handleAttachFilesToAsset}
									>
										<LuIcon icon={modelIconMapping.add} />
									</AttachmentButton>
								</OrganizationAccessRequired>
							</div>

							<AttachmentList
								attachments={sortedAttachments}
								onDelete={handleDeleteAssetAttachment}
								service={sdk.assetAttachments}
							/>
						</section>
					</SegmentedTabs.Content>
				</SegmentedTabs.Root>
			</div>

			<AssetPdfExportOptionsDialog
				key={assetType.uuid}
				assetTypeId={assetType.uuid}
				onSubmitOptions={handleExportAssetToPdf}
				open={showPdfExportOptionsDialog}
				onOpenChange={closePdfExportOptionsDialog}
			/>

			<IssueCommandMenu
				issuesAndIdentifiers={issuesAndIdentifiers}
				issueTypes={issueTypes}
				onIssueSelect={handleLinkIssue}
				open={showIssueCommandMenu}
				onOpenChange={closeIssueCommandMenu}
				onCreateIssue={handleCreateIssueAndLink}
			/>

			<FormSubmissionCommandMenu
				open={showFormCommandMenu}
				onOpenChange={closeFormCommandMenu}
				formSubmissionsAndIdentifiers={formSubmissionsAndIdentifiers}
				forms={forms}
				onSelectFormSubmission={handleSelectFormSubmission}
				onCreateFormSubmission={handleCreateFormSubmission}
			/>
		</>
	)
})
AssetDetailsViewer.displayName = "AssetDetailsViewer"
